import { useState, useEffect } from "react";
import { ListStore } from "./types";
import { Input } from "../../../components/ui/input";
import { useDebounce } from "../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";

interface ListFilteringProps {
  store: ListStore;
  minChars?: number;
  debounceMs?: number;
  placeholder?: string;
}

export function ListFiltering({
  store,
  minChars = 3,
  debounceMs = 500,
  placeholder,
}: ListFilteringProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(store.filters.search || "");
  const debouncedValue = useDebounce(inputValue, debounceMs);

  useEffect(() => {
    const trimmedValue = debouncedValue.trim();
    const storeValue = store.filters.search;

    // need to check if the value is changed and if the value is valid after trimming
    const isValueChanged =
      debouncedValue !== storeValue && storeValue !== trimmedValue;

    const isValidInput =
      trimmedValue.length >= minChars || trimmedValue === "";

    if (isValueChanged && isValidInput) {
      store.setFilter("search", trimmedValue);
    }
  }, [debouncedValue, store, minChars]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Get the trimmed value for validation
  const trimmedInputValue = inputValue.trim();

  return (
    <div className="mb-4 flex-col gap-4">
      <div className="flex flex-1 items-center gap-4">
        <Input
          placeholder={placeholder || t("common.search")}
          value={inputValue}
          onChange={handleInputChange}
          className="max-w-sm"
        />
      </div>

      {inputValue.length > 0 && trimmedInputValue.length < minChars && (
        <span className="text-xs text-muted-foreground">
          {t("validation.minLength", { count: minChars })}
        </span>
      )}
    </div>
  );
}
